import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useACCourseService,
  useCareCaseService,
  type ACCourseDashboardStats,
  type CareDashboardStats,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { OVERVIEW_POLL_MS, type OverviewDomain } from '../constants';

/**
 * useOverviewStats — the org-wide dashboard stat block behind an Overview page's KPI band.
 *
 * `CareDashboardStats` and `ACCourseDashboardStats` are both server-computed from cached
 * data, so they are cheap enough to poll. Everything else on a page (charts, workload,
 * critical lists) refreshes on demand only.
 *
 * Only the requested `domain` is fetched, so the Grow Overview never polls care statistics.
 */
export interface UseOverviewStatsResult {
  careStats: CareDashboardStats | null;
  courseStats: ACCourseDashboardStats | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useOverviewStats(domain: OverviewDomain): UseOverviewStatsResult {
  const careCaseService = useCareCaseService();
  const courseService = useACCourseService();

  const [careStats, setCareStats] = useState<CareDashboardStats | null>(null);
  const [courseStats, setCourseStats] = useState<ACCourseDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      if (domain === 'care') {
        try {
          setCareStats(await careCaseService.dashboardStats());
        } catch (e) {
          console.error('Error fetching care dashboard stats:', e);
          if (showLoading) toast.error('Error', 'Could not load counselling statistics.');
        }
      } else {
        try {
          setCourseStats(await courseService.dashboardStats());
        } catch (e) {
          console.error('Error fetching course dashboard stats:', e);
          if (showLoading) toast.error('Error', 'Could not load course statistics.');
        }
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [careCaseService, courseService, domain]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Poll silently. Kept in a ref so the interval isn't torn down and rebuilt on
  // every render of the parent.
  const fetchRef = useRef(fetchStats);
  fetchRef.current = fetchStats;

  useEffect(() => {
    const id = window.setInterval(() => { fetchRef.current(false); }, OVERVIEW_POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  const refresh = useCallback(() => fetchStats(false), [fetchStats]);

  return { careStats, courseStats, loading, refresh };
}

export default useOverviewStats;
